// Reqqy Backend API Client
// All execution flows go through this client.
// The backend is the source of truth for runs, events, and artifacts.

import { config } from './config';
import type { RunStatus, ModuleType } from './types';

const BASE_URL = config.backendUrl;

/* ── Error class ── */

export class ReqqyBackendError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public path?: string,
    public responseBody?: string,
  ) {
    super(message);
    this.name = 'ReqqyBackendError';
  }
}

/* ── Low-level helpers ── */

async function get<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`);
  } catch (err) {
    throw new ReqqyBackendError(
      `Network error GET ${path}: ${err instanceof Error ? err.message : String(err)}`,
      undefined,
      path,
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ReqqyBackendError(
      `GET ${path} failed: ${res.status} ${text}`,
      res.status,
      path,
      text,
    );
  }
  return res.json();
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ReqqyBackendError(
      `Network error POST ${path}: ${err instanceof Error ? err.message : String(err)}`,
      undefined,
      path,
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ReqqyBackendError(
      `POST ${path} failed: ${res.status} ${text}`,
      res.status,
      path,
      text,
    );
  }
  return res.json();
}

/* ── Types ── */

export interface CreateRunPayload {
  module_type: ModuleType;
  project_id: string;
  input_payload: Record<string, unknown>;
  context_payload?: Record<string, unknown>;
  config_payload?: Record<string, unknown>;
}

export interface BackendRun {
  id: string;
  module_type?: string;
  status?: RunStatus;
  current_step?: string | null;
  progress_pct?: number | null;
  input_payload?: Record<string, unknown>;
  context_payload?: Record<string, unknown>;
  config_payload?: Record<string, unknown>;
  result_payload?: Record<string, unknown>;
  metrics_payload?: Record<string, unknown>;
  error_payload?: Record<string, unknown>;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  [key: string]: unknown;
}

export interface BackendRunEvent {
  id?: string;
  event_type: string;
  message: string;
  data_payload?: Record<string, unknown>;
  created_at?: string;
}

export interface BackendRunArtifact {
  id: string;
  artifact_type: string;
  label?: string;
  content_json?: unknown;
  created_at?: string;
  [key: string]: unknown;
}

/* ── API Functions ── */

/** POST /api/runs — create a new run */
export function createRun(payload: CreateRunPayload): Promise<BackendRun> {
  return post<BackendRun>('/api/runs', payload);
}

/** POST /api/execute/{runId}?mode=sync — trigger execution */
export function executeRun(runId: string, mode: 'sync' | 'async' = 'sync'): Promise<BackendRun> {
  return post<BackendRun>(`/api/execute/${runId}?mode=${mode}`);
}

/** GET /api/runs/{runId} — fetch run state */
export function getRun(runId: string): Promise<BackendRun> {
  return get<BackendRun>(`/api/runs/${runId}`);
}

/** GET /api/runs/{runId}/events — fetch run events */
export function getRunEvents(runId: string): Promise<BackendRunEvent[]> {
  return get<BackendRunEvent[]>(`/api/runs/${runId}/events`);
}

/** GET /api/runs/{runId}/artifacts — fetch run artifacts */
export function getRunArtifacts(runId: string): Promise<BackendRunArtifact[]> {
  return get<BackendRunArtifact[]>(`/api/runs/${runId}/artifacts`);
}

/** GET /api/artifacts/{artifactId} — fetch a single artifact */
export function getArtifact(artifactId: string): Promise<BackendRunArtifact> {
  return get<BackendRunArtifact>(`/api/artifacts/${artifactId}`);
}

/* ── Terminal status helper ── */

export function isTerminalStatus(status?: string): boolean {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}
